import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateDepartment } from '@/hooks/useDepartments';

const PRESET_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const EditDepartmentModal = ({ open, onClose, department, companyUsers = [] }) => {
  const [form, setForm] = useState({
    name: '',
    code: '',
    description: '',
    owner: '',
    members: [],
    tags: [],
    color: '#3B82F6',
    notes: '',
  });
  const [tagInput, setTagInput] = useState('');

  const updateMutation = useUpdateDepartment();

  // Pre-populate form when department changes
  useEffect(() => {
    if (department) {
      setForm({
        name: department.name || '',
        code: department.code || '',
        description: department.description || '',
        owner: department.owner?._id || department.owner || '',
        members: (department.members || []).map((m) => m._id || m),
        tags: department.tags || [],
        color: department.color || '#3B82F6',
        notes: department.notes || '',
      });
      setTagInput('');
    }
  }, [department]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      handleChange('tags', [...form.tags, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tag) => handleChange('tags', form.tags.filter((t) => t !== tag));

  const toggleMember = (userId) => {
    const already = form.members.includes(userId);
    handleChange('members', already ? form.members.filter((m) => m !== userId) : [...form.members, userId]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error('Department name is required');
      return;
    }
    if (form.code && form.code.trim().length > 10) {
      toast.error('Department code must be 10 characters or fewer');
      return;
    }

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || undefined,
      owner: form.owner || undefined,
      members: form.members,
      tags: form.tags,
      color: form.color || undefined,
      notes: form.notes.trim() || undefined,
    };

    updateMutation.mutate(
      { id: department._id, data: payload },
      { onSuccess: () => onClose() }
    );
  };

  if (!department) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Department</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="edit-dept-name">Name <span className="text-red-500">*</span></Label>
            <Input
              id="edit-dept-name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>

          {/* Code */}
          <div className="space-y-1">
            <Label htmlFor="edit-dept-code">Code <span className="text-xs text-gray-400">(max 10 chars)</span></Label>
            <Input
              id="edit-dept-code"
              value={form.code}
              onChange={(e) => handleChange('code', e.target.value)}
              maxLength={10}
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="edit-dept-desc">Description</Label>
            <Textarea
              id="edit-dept-desc"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
            />
          </div>

          {/* Owner */}
          {companyUsers.length > 0 && (
            <div className="space-y-1">
              <Label>Owner</Label>
              <Select value={form.owner || 'none'} onValueChange={(v) => handleChange('owner', v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No owner</SelectItem>
                  {companyUsers.map((u) => (
                    <SelectItem key={u._id} value={u._id}>{u.name} — {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Members */}
          {companyUsers.length > 0 && (
            <div className="space-y-1">
              <Label>Members</Label>
              <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                {companyUsers.map((u) => (
                  <label key={u._id} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-gray-50 dark:hover:bg-gray-800 px-1 py-0.5 rounded">
                    <input
                      type="checkbox"
                      checked={form.members.includes(u._id)}
                      onChange={() => toggleMember(u._id)}
                      className="rounded"
                    />
                    {u.name} — {u.email}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="space-y-1">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add tag and press Enter"
              />
              <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {form.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Color */}
          <div className="space-y-1">
            <Label>Color</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => handleChange('color', c)}
                  className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    borderColor: form.color === c ? '#1f2937' : 'transparent',
                  }}
                />
              ))}
              <Input
                type="text"
                value={form.color}
                onChange={(e) => handleChange('color', e.target.value)}
                placeholder="#3B82F6"
                className="w-28 h-7 text-xs"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label htmlFor="edit-dept-notes">Notes</Label>
            <Textarea
              id="edit-dept-notes"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditDepartmentModal;
